import type { TreeItemComponentProps } from '@neo4j-ndl/react';
import { Checkbox, Skeleton, Tooltip, TreeView, Typography } from '@neo4j-ndl/react';
import { ChevronDownIconSolid, ChevronRightIconSolid } from '@neo4j-ndl/react/icons';
import { isNotNullish } from '@nx/stdlib';
import React from 'react';

export type TreeDataProps = {
  id: string;
  text: string;
  entityType: 'organization' | 'project' | 'instance';
  isDisabled?: boolean;
  disabledReason?: string;
};

// eslint-disable-next-line react/display-name
const CustomTreeItemComponent = React.forwardRef<HTMLElement, TreeItemComponentProps<TreeDataProps>>((props, ref) => {
  return (
    <TreeView.TreeItemWrapper
      {...props}
      onItemsChanged={(newItems, itemChangedReason) => {
        props.onItemsChanged(newItems, itemChangedReason);
      }}
      ref={ref}
    >
      {props.item.canHaveSubItems && (
        <button onClick={() => props.onCollapse?.()} className="n-flex n-items-center n-gap-2">
          {props.item.isCollapsed ? (
            <ChevronRightIconSolid className="n-h-5 n-w-5" />
          ) : (
            <ChevronDownIconSolid className="n-h-5 n-w-5" />
          )}
        </button>
      )}
      {(props.item.data.isDisabled ?? false) ? (
        <Tooltip type="simple">
          <Tooltip.Trigger hasButtonWrapper>
            <Checkbox isDisabled />
          </Tooltip.Trigger>
          <Typography className="text-neutral-text-weak" variant="body-medium">
            {props.item.data.text}
          </Typography>
          <Tooltip.Content>{props.item.data.disabledReason}</Tooltip.Content>
        </Tooltip>
      ) : (
        <Skeleton
          onBackground="weak"
          variant="rectangular"
          {...props.item.skeletonProps}
          isLoading={props.item.isSkeletonLoading}
        >
          <div className="n-items-center n-flex n-flex-row gap-1">
            {isNotNullish(props.item.isSelected) && (
              <Checkbox
                isDisabled={props.item.data.isDisabled}
                isChecked={props.item.isSelected}
                ariaLabel={props.item.data.text}
                onChange={(event) => {
                  const newItems = props.items.map((i) => {
                    if (i.id === props.item.id) {
                      return {
                        ...i,
                        isSelected: event.target.checked,
                      };
                    }
                    return i;
                  });

                  // Uncheck parents if a child is unchecked
                  if (!event.target.checked) {
                    let { parentId } = props.item;
                    while (parentId !== null) {
                      // eslint-disable-next-line no-loop-func
                      const parent = newItems.find((newItem) => newItem.id === parentId);
                      if (parent) {
                        newItems.forEach((newItem) => {
                          if (newItem.id === parent.id) {
                            newItem.isSelected = false;
                          }
                        });
                        parentId = parent.parentId;
                      } else {
                        parentId = null;
                      }
                    }
                  }
                  // Check children if a parent is checked
                  if (props.item.canHaveSubItems) {
                    const childIdsToUncheck = props.items.filter((i) => i.parentId === props.item.id).map((i) => i.id);
                    while (childIdsToUncheck.length > 0) {
                      const childId = childIdsToUncheck.pop();
                      const child = props.items.find((i) => i.id === childId);
                      if (child) {
                        newItems.forEach((newItem) => {
                          if (newItem.id === child.id) {
                            newItem.isSelected = event.target.checked;
                          }
                        });
                        newItems
                          .filter((i) => i.parentId === child.id)
                          .forEach((i) => {
                            childIdsToUncheck.push(i.id);
                          });
                      }
                    }
                  }
                  props.onItemsChanged(newItems, { item: props.item, reason: 'selected' });
                }}
              />
            )}
            <Typography variant="body-medium">{props.item.data.text}</Typography>
          </div>
        </Skeleton>
      )}
    </TreeView.TreeItemWrapper>
  );
});

export default CustomTreeItemComponent;
